import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que recopilamos',
    body: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, correo electrónico y datos de pago. No almacenamos datos de tarjetas de crédito directamente; estos son procesados de forma segura por nuestro proveedor de pagos.'
  }, {
    heading: 'Uso de la información',
    body: 'Tu información se utiliza exclusivamente para: entregar el producto digital adquirido, enviarte actualizaciones relacionadas con tu compra, y mejorar nuestros servicios. No vendemos, alquilamos ni compartimos tu información personal con terceros.'
  }, {
    heading: 'Cookies',
    body: 'Utilizamos cookies esenciales para el funcionamiento del sitio y cookies analíticas para entender cómo los visitantes interactúan con la página. Puedes desactivar las cookies en la configuración de tu navegador.'
  }, {
    heading: 'Seguridad',
    body: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, pérdida o alteración.'
  }, {
    heading: 'Tus derechos',
    body: 'Tienes derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento. Para ejercer estos derechos, puedes contactarnos directamente.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto digital',
    body: 'El Miedo Diario es un producto digital en formato PDF. Al completar tu compra, recibirás acceso inmediato al material. Dado que se trata de un producto digital descargable, no se aceptan devoluciones una vez entregado el acceso.'
  }, {
    heading: 'Uso permitido',
    body: 'El contenido adquirido es para uso personal y no transferible. Queda prohibida la reproducción, distribución, modificación o reventa del material sin autorización expresa por escrito.'
  }, {
    heading: 'Propiedad intelectual',
    body: 'Todo el contenido, incluyendo textos, diseños, gráficos y metodologías presentadas en El Miedo Diario, está protegido por derechos de autor. El uso no autorizado constituye una violación de la ley de propiedad intelectual.'
  }, {
    heading: 'Descargo de responsabilidad',
    body: 'El Miedo Diario es un recurso educativo y de desarrollo personal. No sustituye asesoramiento médico, psicológico o profesional. Los resultados pueden variar según cada persona. No garantizamos resultados específicos.'
  }, {
    heading: 'Modificaciones',
    body: 'Nos reservamos el derecho de actualizar estos términos en cualquier momento. Las modificaciones entrarán en vigor desde su publicación en esta página.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-43">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-44" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      y: 30,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} onClick={e => e.stopPropagation()} className="relative bg-dark-800 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" data-id="element-45">
        {/* Header */}
        <div className="sticky top-0 bg-dark-800 border-b border-white/5 px-6 py-5 flex items-center justify-between z-10" data-id="element-46">
          <h2 className="text-xl font-bold text-white" data-id="element-47">{content.title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors" aria-label="Cerrar" data-id="element-48">
            <X size={20} data-id="element-49" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-72px)] space-y-8" data-id="element-50">
          {content.sections.map((section, index) => <div key={index} data-id="element-51">
              <h3 className="text-amber-gold font-semibold text-base mb-2" data-id="element-52">
                {section.heading}
              </h3>
              <p className="text-gray-400 leading-relaxed text-[15px]" data-id="element-53">
                {section.body}
              </p>
            </div>)}

          <p className="text-gray-600 text-xs pt-4 border-t border-white/5" data-id="element-54">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-10 bg-dark-900 border-t border-white/5" data-id="element-55">
        <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-56">
          <p className="text-gray-600 text-sm" data-id="element-57">
            © {new Date().getFullYear()} El Miedo Diario. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-58">
            <button onClick={() => setActiveModal('privacy')} className="text-gray-500 hover:text-amber-gold text-sm transition-colors" data-id="element-59">
              Política de Privacidad
            </button>
            <span className="text-gray-700" data-id="element-60">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-gray-500 hover:text-amber-gold text-sm transition-colors" data-id="element-61">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-62">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-63" />}
      </AnimatePresence>
    </>;
}